import { v7 as uuid } from "uuid";
import { AudioBufferSource } from "./audio-buffer-source";
import { Source } from "./Source";
import { sliceAudioBuffer } from "./utils/sliceAudioBuffer";

/**
 * Information about a region for punch-in calculations.
 */
export interface RegionInfo {
  id: string;
  position: number;
  duration: number;
}

interface TrackProps {
  id: string;
  audioContext: AudioContext;
  destination: AudioNode;
}

interface RegionProps {
  id: string;
  audioBuffer: AudioBuffer;
  position: number;
}

/**
 * A Track represents a single audio track with one or more regions.
 *
 * Tracks are created empty and regions are added via addRegion().
 * Regions receive pre-decoded AudioBuffers (loading is handled by RegionLoader).
 */
export class Track {
  id: string;

  private sources: Source[] = [];
  private gainNode: GainNode;
  private audioContext: AudioContext;

  constructor({ id, audioContext, destination }: TrackProps) {
    this.id = id;
    this.audioContext = audioContext;

    this.gainNode = destination.context.createGain();
    this.gainNode.connect(destination);
  }

  /**
   * Add a region to this track.
   *
   * @param props.id - Unique ID for the region
   * @param props.audioBuffer - Pre-decoded AudioBuffer for playback
   * @param props.position - Start position in seconds within the song timeline
   */
  addRegion({ id, audioBuffer, position }: RegionProps): void {
    const source = new AudioBufferSource({
      id,
      audioBuffer,
      position,
      destination: this.gainNode,
      audioContext: this.audioContext,
    });

    this.sources.push(source);
  }

  play(timestamp: number, playbackRate: number = 1.0) {
    this.sources.forEach((source) => source.play(timestamp, playbackRate));
  }

  pause() {
    this.sources.forEach((source) => source.pause());
  }

  setGain(gain: number) {
    this.gainNode.gain.value = gain;
  }

  async seek(timestamp: number) {
    return Promise.all(this.sources.map((source) => source.seek(timestamp)));
  }

  destroy() {
    // Disconnect the gain node
    if (this.gainNode) {
      this.gainNode.disconnect();
    }

    this.sources = [];
  }

  // ============================================================================
  // Region Manipulation (for punch-in recording)
  // ============================================================================

  /**
   * Get all regions that overlap with a time range.
   */
  getRegionsInRange(start: number, end: number): RegionInfo[] {
    return this.sources
      .filter((source) => {
        const regionEnd = source.position + source.duration;
        // Overlaps if: region starts before range ends AND region ends after range starts
        return source.position < end && regionEnd > start;
      })
      .map((source) => ({
        id: source.id,
        position: source.position,
        duration: source.duration,
      }));
  }

  /**
   * Get a source by ID.
   */
  getSource(id: string): AudioBufferSource | undefined {
    return this.sources.find((s) => s.id === id) as AudioBufferSource | undefined;
  }

  /**
   * Remove a region from the track.
   * Stops playback and disconnects the source before removing.
   */
  removeRegion(id: string): void {
    const idx = this.sources.findIndex((s) => s.id === id);
    if (idx >= 0) {
      // Stop playback and disconnect before removing
      this.sources[idx].pause();
      this.sources.splice(idx, 1);
    }
  }

  /**
   * Truncate a region to a new duration (keeping from start).
   * Uses buffer slicing for clean audio.
   */
  truncateRegion(id: string, newDuration: number): void {
    const source = this.getSource(id);
    if (!source) throw new Error(`Region ${id} not found`);

    const slicedBuffer = sliceAudioBuffer(
      source.audioBuffer,
      0,
      newDuration,
      source.audioContext
    );
    source.replaceBuffer(slicedBuffer);
  }

  /**
   * Trim the start of a region (keeping from offset to end).
   * Updates position and uses buffer slicing.
   *
   * @param id - Region ID
   * @param trimAmount - Amount to trim from the start (in seconds)
   */
  trimRegionStart(id: string, trimAmount: number): void {
    const source = this.getSource(id);
    if (!source) throw new Error(`Region ${id} not found`);

    const newDuration = source.duration - trimAmount;
    const newPosition = source.position + trimAmount;

    const slicedBuffer = sliceAudioBuffer(
      source.audioBuffer,
      trimAmount,
      newDuration,
      source.audioContext
    );

    source.replaceBuffer(slicedBuffer);
    source.setPosition(newPosition);
  }

  /**
   * Split a region, creating a new "after" portion.
   * The original region is truncated to end at splitPoint.
   * A new region is created starting at splitPoint.
   *
   * @param id - Region ID to split
   * @param splitPoint - Position in the song timeline where to split
   * @returns Info about the newly created region
   */
  splitRegion(id: string, splitPoint: number): RegionInfo {
    const source = this.getSource(id);
    if (!source) throw new Error(`Region ${id} not found`);

    const regionEnd = source.position + source.duration;

    // Calculate durations for both pieces
    const beforeDuration = splitPoint - source.position;
    const afterDuration = regionEnd - splitPoint;
    const afterOffset = splitPoint - source.position;

    // Create the "after" buffer
    const afterBuffer = sliceAudioBuffer(
      source.audioBuffer,
      afterOffset,
      afterDuration,
      source.audioContext
    );

    // Truncate the original to be the "before" piece
    this.truncateRegion(id, beforeDuration);

    // Create the "after" region with a new ID
    const afterId = uuid();
    this.addRegion({
      id: afterId,
      audioBuffer: afterBuffer,
      position: splitPoint,
    });

    return {
      id: afterId,
      position: splitPoint,
      duration: afterDuration,
    };
  }
}
